import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FcpToastService, TooltipPosition, TooltipPositionX, TooltipPositionY } from 'fc-ui-components';
import { CopyEvent } from './matrix.component';
import { MatrixType } from './matrix.module';

const SCROLL_ICON_VISIBILITY_THRESHOLD = 5;

@Component({
    selector: 'thead[fc-order-matrix-headers]',
    templateUrl: './matrix-headers.component.html',
    styleUrls: ['./matrix-headers.component.less'],
    standalone: false
})
export class OrderMatrixHeadersComponent implements OnChanges {
    @Input() headers: Array<string | number>;
    @Input() groupedHeaders?: Array<GroupedHeader>;
    @Input() useGroupedHeaders = false;
    @Input() invertGroupedHeaders = false;
    @Input() tooltips: Array<string>;
    @Input() headerStoreIds: Array<string>;
    @Input() headerHoverDisplayMap: { [size: string]: string };
    @Input() enableHoverAndCopy = false;
    @Input() type: MatrixType;
    @Input() capitalizeHeaders = true;
    @Input() showCopy = false;
    @Input() useTooltip = false;
    @Input() hideFirstCell = false;
    @Input() useStickyHeader = true;

    @Output() onCopy: EventEmitter<CopyEvent>;

    public MatrixType = MatrixType;
    public Direction = Direction;
    public displayTooltip: boolean[];
    public displayCopyDropdown: boolean[] = [];
    public hoveredHeaderKey: string | null = null;
    public tooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Bottom
    };

    isScrollIconVisible: {
        [Direction.Left]?: boolean;
        [Direction.Right]?: boolean;
    } = {};

    scrollIconStylePx = {
        [Direction.Left]: { left: 5, top: 15 },
        [Direction.Right]: { right: 0, top: 15 }
    };

    scrollIconVisibleTimeoutCallback: {
        [Direction.Left]?: NodeJS.Timeout;
        [Direction.Right]?: NodeJS.Timeout;
    } = {};

    constructor(
        private elementRef: ElementRef,
        private fcpToastService: FcpToastService,
        private translateService: TranslateService
    ) {
        this.onCopy = new EventEmitter();
    }

    ngOnChanges() {
        if (!this.hideFirstCell) {
            // first header is a fixed empty header
            this.headers.unshift('');
            this.tooltips.unshift('');
            this.headerStoreIds.unshift('');
        }
        this.resetTooltipsDisplay();
        this.initializeEanHoverState();
        // we need setTimeout because without it change detection mechanism will be triggered too early
        setTimeout(() => {
            this.scrollHandler(this.elementRef.nativeElement.closest('div.fc-order-matrix-wrapper'));
        });
    }

    onScroll(event: Event) {
        const target = event.target;
        this.scrollHandler(target as Element);
    }

    scrollMatrix(direction: Direction) {
        const headerElement: HTMLTableRowElement = this.elementRef.nativeElement.getElementsByTagName('tr')[0];
        const tableWrapperElement = this.elementRef.nativeElement.closest('div.fc-order-matrix-wrapper');

        if (headerElement) {
            const columnElements = headerElement.children;
            const newScrollLeft =
                (direction === Direction.Left ? -1 : 1) * columnElements[1].clientWidth * 5 +
                tableWrapperElement.scrollLeft;
            tableWrapperElement.scrollLeft = newScrollLeft - (newScrollLeft % columnElements[1].clientWidth);
        }
    }

    isIcon(value: any) {
        if (!value || typeof value !== 'string') {
            return false;
        }
        return value.split(' ')[0].substring(0, 2) === 'fa';
    }

    resetTooltipsDisplay() {
        this.displayTooltip = Array(this.headers.length).fill(false);
    }

    resetCopyDropdownDisplay() {
        this.displayCopyDropdown = Array(this.headers.length).fill(false);
    }

    showCopyDropdownMenu(index: number) {
        this.resetCopyDropdownDisplay();
        this.displayCopyDropdown[index] = true;
    }

    onCopyDropdownMenuClick(sourceStoreId: string, columnIndex: number) {
        this.onCopy.emit({ sourceStoreId, columnIndex });
    }

    @HostListener('document:click', ['$event.target'])
    clickOutsideComponentListener(targetElement: HTMLElement) {
        if (!targetElement.closest('#copy-icon')) {
            this.resetCopyDropdownDisplay();
        }
    }

    public toggleTooltip(value: boolean, index: number) {
        this.resetTooltipsDisplay();
        if (this.tooltips[index]) {
            this.displayTooltip[index] = value;
        }
    }

    public toggleEanDisplay(value: boolean, key: string) {
        this.hoveredHeaderKey = value ? key : null;
    }

    public isHeaderHovered(key: string): boolean {
        return this.hoveredHeaderKey === key;
    }

    public getHeaderDisplayText(headerKey: string, originalText: string): string {
        if (this.enableHoverAndCopy && this.isHeaderHovered(headerKey) && this.hasEanMapping(headerKey)) {
            return this.getHoverText(headerKey);
        }
        return originalText;
    }

    public hasEanMapping(key: string): boolean {
        const headerLabel = this.getHeaderLabel(key);
        return !!(this.headerHoverDisplayMap && this.headerHoverDisplayMap[headerLabel]);
    }

    public async copyHeaderText(headerKey: string, event: MouseEvent) {
        event.stopPropagation();

        const clipboardText = this.isHeaderHovered(headerKey)
            ? this.getHoverText(headerKey)
            : this.getOriginalHeaderText(headerKey);

        try {
            await navigator.clipboard.writeText(clipboardText);
            const toastText = this.translateService.instant('SMART_REPLENISHMENT.COPY_EAN_TOAST_SUCCESS', {
                clipboardText
            });
            this.fcpToastService.show({ text: toastText });
        } catch (error) {
            console.error('Failed to copy text to clipboard:', error);
            const toastText = this.translateService.instant('SMART_REPLENISHMENT.COPY_EAN_TOAST_FAILED');
            this.fcpToastService.show({ text: toastText });
        }
    }

    private getHoverText(headerKey: string): string {
        const headerLabel = this.getHeaderLabel(headerKey);
        return this.headerHoverDisplayMap?.[headerLabel] || '';
    }

    private getOriginalHeaderText(headerKey: string): string {
        if (headerKey.startsWith(HeaderPrefix.Standard)) {
            const header = this.getHeaderLabel(headerKey);
            return this.capitalizeHeaders ? header.toUpperCase() : header;
        }

        if (headerKey.startsWith(HeaderPrefix.Grouped)) {
            const parts = headerKey.split('-');
            const groupIndex = Number(parts[2]);
            const childIndex = Number(parts[3]);
            const group = this.groupedHeaders?.[groupIndex];
            const childLabel = group?.childLabels[childIndex] || '';

            // For grouped headers, display with prefix (e.g., "L34") not the full size
            return `${group?.childPrefix || ''}${childLabel}`;
        }

        return '';
    }

    private getHeaderLabel(headerKey: string): string {
        if (headerKey.startsWith(HeaderPrefix.Standard)) {
            const index = Number(headerKey.replace(HeaderPrefix.Standard, ''));
            return this.headers?.[index]?.toString() || '';
        }

        if (headerKey.startsWith(HeaderPrefix.Grouped)) {
            const parts = headerKey.split('-');
            const groupIndex = Number(parts[2]);
            const childIndex = Number(parts[3]);

            const group = this.groupedHeaders?.[groupIndex];
            const childLabel = group?.childLabels[childIndex];

            return group && childLabel ? `${group.parentLabel}/${childLabel}` : '';
        }

        return '';
    }

    private initializeEanHoverState() {
        this.hoveredHeaderKey = null;
    }

    private scrollHandler(target: Element) {
        this.calculateVisibilityScrollIcon(target, Direction.Left);
        this.calculateVisibilityScrollIcon(target, Direction.Right);
    }

    private calculateVisibilityScrollIcon(target: Element, direction: Direction) {
        const spaceLeftByXAxis = this.calculateSpaceLeftByXAxis(target, direction);
        // we need to keep absolute positioned element in place
        this.scrollIconStylePx[direction] = this.calculateScrollIconPosition(target, direction);

        if (spaceLeftByXAxis > SCROLL_ICON_VISIBILITY_THRESHOLD) {
            this.isScrollIconVisible[direction] = false;
            if (this.scrollIconVisibleTimeoutCallback[direction]) {
                clearTimeout(this.scrollIconVisibleTimeoutCallback[direction]);
            }
            this.scrollIconVisibleTimeoutCallback[direction] = setTimeout(() => {
                if (this.calculateSpaceLeftByXAxis(target, direction) > SCROLL_ICON_VISIBILITY_THRESHOLD) {
                    this.isScrollIconVisible[direction] = true;
                }
            }, 200);

            return;
        }
        this.isScrollIconVisible[direction] = false;
    }

    private calculateSpaceLeftByXAxis(target: Element, direction: Direction) {
        if (direction === Direction.Left) {
            return Math.floor(target.scrollLeft);
        }
        return Math.floor(target.scrollWidth - target.scrollLeft - target.clientWidth);
    }

    private calculateScrollIconPosition(target: Element, direction: Direction) {
        if (direction === Direction.Left) {
            return {
                left: 5 + target.scrollLeft,
                top: 15 + target.scrollTop,
                right: undefined
            };
        }
        return {
            right: -target.scrollLeft,
            top: 15 + target.scrollTop,
            left: undefined
        };
    }
}

enum HeaderPrefix {
    Standard = 'standard-header-',
    Grouped = 'grouped-header-'
}

enum Direction {
    Left,
    Right
}

export interface GroupedHeader {
    parentLabel: string;
    childLabels: string[];
    hoverTexts?: string[];
    parentPrefix?: string;
    childPrefix?: string;
}
